import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Float, PointMaterial, Points, Stars } from '@react-three/drei';

const GOLD = '#D4AF37';
const NAVY = '#1E252F';

function GoldDust({ count = 420 }) {
  const group = useRef(null);
  const positions = useMemo(() => {
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 24;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 14;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 12;
    }
    return pos;
  }, [count]);

  useFrame((_, delta) => {
    if (group.current) group.current.rotation.y += delta * 0.035;
  });

  return (
    <group ref={group}>
      <Points positions={positions} stride={3} frustumCulled={false}>
        <PointMaterial
          transparent
          color={GOLD}
          size={0.045}
          sizeAttenuation
          depthWrite={false}
          opacity={0.42}
        />
      </Points>
    </group>
  );
}

function AccentShape() {
  const meshRef = useRef(null);
  useFrame((_, delta) => {
    if (meshRef.current) meshRef.current.rotation.x += delta * 0.08;
  });
  return (
    <Float speed={1.8} rotationIntensity={0.18} floatIntensity={0.35}>
      <mesh ref={meshRef}>
        <torusKnotGeometry args={[0.95, 0.28, 120, 16]} />
        <meshStandardMaterial
          color={GOLD}
          metalness={0.55}
          roughness={0.35}
          wireframe
          transparent
          opacity={0.22}
        />
      </mesh>
    </Float>
  );
}

/** Hero / full-bleed cinematic scene */
export default function HeroScene() {
  const group = useRef(null);
  useFrame((_, delta) => {
    if (group.current) group.current.rotation.z += delta * 0.012;
  });

  return (
    <group ref={group}>
      <ambientLight intensity={0.35} />
      <directionalLight position={[6, 8, 6]} intensity={0.85} color="#ffffff" />
      <pointLight position={[-4, -2, 4]} intensity={0.6} color={GOLD} />
      <pointLight position={[4, 2, -6]} intensity={0.35} color={NAVY} />
      <GoldDust />
      <AccentShape />
      <Stars radius={60} depth={36} count={900} factor={2.2} saturation={0} fade speed={0.35} />
    </group>
  );
}
